import React, { useEffect, useState } from 'react';
import {
    TextField,
    Typography,
    Stack,
    List,
    ListItem,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Button,
    Rating,
    AvatarGroup,
    Avatar,
    Divider,
    Grid,
    Tooltip
} from "@mui/material";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { LoadingButton } from '@mui/lab';
import Div from "@jumbo/shared/Div";
import _ from 'lodash';
import deliveryServices from 'app/services/delivery-services';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ImageViewer from '../Images/ImageViewer';
import SignatureCanvas from '../Shared/SignatureCanvas/SignatureCanvas';
import { useSnackbar } from 'notistack';

const BoolToCheck = (props) => {
    if(props.value === true) {
        return (<CheckCircleRoundedIcon fontSize="small" color='success'/>)
    } else {
        return (<CancelRoundedIcon fontSize="small" color='error'/>)
    }
}

const DeliveryDetails = props => {
    const { enqueueSnackbar } = useSnackbar()
    const { setListLoading } = props;
    const [saving, setSaving] = useState(false)
    const [editing, setEditing] = useState(false)
    const [canSave, setCanSave] = useState(false)
    const [details, setDetails] = useState(null)
    const [detailsLoading, setDetailsLoading] = useState(false)
    const [openImageViewer, setOpenImageViewer] = useState(false)
    const [currentDetail, setCurrentDetail] = useState([])
    const [confirmFinishDialogOpen, setConfirmFinishDialogOpen] = useState(false);

    const confirmAndSave = () => {
        const currentState = props.delivery?.state;
        if (['pending', 'in_progress'].includes(currentState)) {
            setConfirmFinishDialogOpen(true);
        } else {
            saveDeliveryDetails();
        }
    };    

    const saveDeliveryDetails = (forceFinish = false) => {
        setSaving(true);
        const payload = { details: details };
    
        if (forceFinish) {
            payload.new_state = 'finished';
        }
    
        deliveryServices.updateDeliveryDetails(props.delivery?.id, payload)
            .then(() => {
                return deliveryServices.getDeliveryDetails(props.delivery?.id);
            })
            .then((updatedDetails) => {
                setDetails(updatedDetails);
                setEditing(false);
                setCanSave(false);
                setSaving(false);
                enqueueSnackbar("Detalles actualizados correctamente", { variant: 'success' });
                props.onSave?.();
                setListLoading?.(true);
            })
            .catch((err) => {
                setSaving(false);
                enqueueSnackbar("Error al guardar los detalles", { variant: 'error' });
                console.error(err);
            });
    };    

     useEffect(() => {
         if(props.delivery) {
            setDetailsLoading(true);
            deliveryServices.getDeliveryDetails(props.delivery?.id)
                .then((data) => {
                    setDetails(data)
                    setDetailsLoading(false);
                });
        }
    }, [])

    const renderDetails = () => {
        if(details) {
            return(
                <List
                    dense={true}
                    sx={{ padding: 0 }}>
                    {_.map(details, (d) => {
                        return (
                            <ListItem
                                key={d.id}
                                id={d.id}
                                sx={{alignItems: 'flex-start'}}>
                                    <ListItemText id={d.id}
                                        primary={
                                            <Div>    
                                                <Stack direction="row" spacing={2}>
                                                    <Typography sx={{ fontFamily: 'Monospace' }}>{d.product_code}</Typography>
                                                    <Stack direction="column" spacing={0}>
                                                        <Typography>{d.product}</Typography>
                                                        { d.observations ? 
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Typography color="primary" sx={{ fontWeight: 'bold' }}>Obs</Typography>
                                                                <Typography>{d.observations}</Typography>
                                                            </Stack>
                                                            : null
                                                        }
                                                        { renderImagePreviews(d) }
                                                    </Stack>
                                                </Stack>
                                            </Div>
                                        }/>
                                    <Stack direction="row" justifyContent="space-between">
                                        { d.reception_msg ? <WarningAmberRoundedIcon color={d.reception_msg.kind} /> : null }
                                        { editing ?
                                            <TextField error={ d.error === true } variant="standard" defaultValue={ d.delivered_qty } onChange={ (event) => { handleEdition(d.id, event.target.value) } } sx={{ width: '3ch' }} />
                                            :
                                            <Typography sx={{ minWidth: 15, textAlign: 'right' }}>{ d.delivered_qty }</Typography>
                                        }
                                        <Typography sx={{minWidth: 12, textAlign: 'right'}}>/</Typography>
                                        <Typography sx={{minWidth: 18, textAlign: 'right'}}>{d.qty}</Typography>
                                        <Typography sx={{minWidth: 25, textAlign: 'right'}}>{d.meassure_unit}</Typography>
                                    </Stack>
                            </ListItem>
                        )
                    })}
                </List>
            )
        }
    }

    const handleEdition = (detailId, newDeliveredQty) => {
        const qtyInt = parseInt(newDeliveredQty);
        let exceeded = false;
    
        const nextDetails = details.map((d) => {
            if (d.id == detailId) {
                const error = (qtyInt < 0) || (qtyInt > parseInt(d.qty));
                if (qtyInt > parseInt(d.qty)) {
                    exceeded = true;
                }
                return {
                    ...d,
                    delivered_qty: qtyInt,
                    error: error,
                };
            } else {
                return d;
            }
        });
    
        setDetails(nextDetails);
        saveButtonDisabled();
    
        if (exceeded) {
            enqueueSnackbar("Excede máximo", { variant: 'error' });
        }
    };
    

    const saveButtonDisabled = () => {
        let detailsError = false;
        details.map((d) => {
            if(d.error === true) {
                detailsError = true;
            }
        });
        setCanSave(!detailsError);
    }

    const renderImagePreviews = (_detail) => {
        if(_detail.images) {
            return (
                <AvatarGroup max={3} sx={{justifyContent: 'flex-end', cursor: 'pointer'}} onClick={() => { setOpenImageViewer(true); setCurrentDetail(_detail) } }>
                    { _.map(_detail.images, (im) => {
                        return (<Avatar src={im} />)
                    })}
                </AvatarGroup>
            )
        }
    }

    const renderGeneralDescription = () => {
        if (props.delivery) {
            const relevantDocs = props.delivery.delivery_docs?.filter(dd =>
                ['taxed-invoice', 'delivery-receipt'].includes(dd.shipping_doc?.shipping_doc_kind?.slug)
            ) || [];
    
            const prioritizedDoc = relevantDocs.find(dd => dd.shipping_doc?.shipping_doc_kind?.slug === 'taxed-invoice') || relevantDocs.find(dd => dd.shipping_doc?.shipping_doc_kind?.slug === 'delivery-receipt');
    
            return (
                <Div sx={{ mb: 3 }}>
                    <Typography variant={"h4"}>Descripción general</Typography>
                    <Stack sx={{ px: 2 }}>
                        <Stack direction="row">
                            <Typography sx={{ minWidth: 150, fontWeight: 'medium' }}>Nº Pedido:</Typography>
                            <Typography>{props.delivery.order?.code}</Typography>
                        </Stack>
                        {prioritizedDoc && (
                            <Stack direction="row">
                                <Typography sx={{ minWidth: 150, fontWeight: 'medium' }}>
                                    {prioritizedDoc.shipping_doc?.shipping_doc_kind?.slug === 'delivery-receipt'? 'Nº Guía:' : 'Nº Factura:'}
                                </Typography>
                                <Typography>{prioritizedDoc.shipping_doc?.customer_code}</Typography>
                            </Stack>
                        )}
                    </Stack>
                </Div>
            );
        }
    };
    

    const getPreferredReceipt = () => {
        const receipts = props.delivery?.receipts || [];
        return (
            receipts.find(r => r.shipping_doc_kind_id === 4) || receipts.find(r => r.shipping_doc_kind_id === null)
        );
    };

    const getSignedReceipt = () => {
        const receipts = props.delivery?.receipts || [];
        return receipts.find(r => r.shipping_doc_kind_id === 4);
    };    

    const renderReceptorDetails = () => {
        if(props.delivery?.receipts) {
            return(
                <Div sx={{mb: 3}}>
                    <Typography variant={"h4"}>Recibido por</Typography>
                    <Stack sx={{px: 2}}>
                        <Stack direction="row"><Typography sx={{minWidth: 150, fontWeight: 'medium' }}>Nombre:</Typography>{getPreferredReceipt()?.name}</Stack>
                        <Stack direction="row"><Typography sx={{minWidth: 150, fontWeight: 'medium' }}>RUT:</Typography>{getPreferredReceipt()?.tin}</Stack>
                        <Stack direction="row"><Typography sx={{minWidth: 150, fontWeight: 'medium' }}>Observaciones:</Typography>{getPreferredReceipt()?.observations}</Stack>
                    </Stack>
                </Div>
            )
        }
    }

    const renderSurveyAnswers = () => {
        if(props.delivery?.survey_answers && props.delivery?.survey_answers.length > 0) {
            return(
                <Div sx={{mb: 3}}>
                    <Typography variant={"h4"}>Resultados encuesta</Typography>
                    {_.map(props.delivery?.survey_answers, (ans) => {               
                        return (
                            <Stack sx={{px: 2, mb: 2}}>
                                <Stack spacing={0.5}>
                                    <Typography sx={{minWidth: 150, fontWeight: 'medium' }}>P: {ans.question_text}</Typography>
                                    <Stack direction="row" alignItems="bottom" spacing={0.5}>
                                        <Typography>R:</Typography>
                                        <Typography>
                                        {ans.kind == 'binary' ?
                                            (ans.binary_value == true ? 'Si' : 'No')
                                        : ans.kind == 'calification' ?
                                            ans.calification
                                        : ans.answer
                                        }
                                        </Typography>
                                        {ans.kind == 'binary' ? <BoolToCheck value={ans.binary_value}/> : null}
                                        {ans.kind == 'calification' ? <Rating name="read-only" value={ans.calification} readOnly size="small" /> : null}
                                    </Stack>
                                </Stack>
                            </Stack>
                        )
                    })}
                </Div>
            )
        }
    }

    const isEditLocked = props.delivery?.regularization_status === true;

    return (
        <React.Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={"md"}
                open={props.isOpen}
                onClose={props.onClose}>
                <DialogTitle>
                    <Div sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                        <Typography variant={"h2"} color="primary">Entrega Nº{props.delivery?.code}</Typography>
                    </Div>
                </DialogTitle>
                <DialogContent>
                    {detailsLoading ?
                        <Div sx={{textAlign: 'center', my: 5}}>
                            <CircularProgress color="warning"/>
                        </Div>
                    :
                        <React.Fragment>
                            <Grid container spacing={2}>
                                <Grid item xs={12} lg={6}>
                                    {renderGeneralDescription()}
                                    {renderReceptorDetails()}
                                </Grid>
                                <Grid item xs={12} lg={6} sx={{textAlign: 'right'}}>
                                    {getPreferredReceipt()?.signature && (
                                        <SignatureCanvas
                                            height={200}
                                            width={200}
                                            signatureData={JSON.parse(getSignedReceipt().signature)}
                                            caption="Firma"
                                        />
                                    )}  
                                </Grid>
                            </Grid>
                            {renderSurveyAnswers()}
                            <Grid container spacing={2}>
                                <Grid item>
                                    <Typography variant={"h4"}>Detalle de productos</Typography>
                                </Grid>
                                <Grid item>
                                <Tooltip
                                    title={ isEditLocked ? "La edición está deshabilitada: la entrega ya fue regularizada." : "Editar entrega" }>
                                    <span>
                                        <Button size="small" variant="contained" disabled={editing === true || isEditLocked} onClick={() => { setEditing(true); }}>
                                            Editar
                                        </Button>
                                    </span>
                                </Tooltip>
                                </Grid>
                            </Grid>
                            {renderDetails()}
                        </React.Fragment>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { props.onClose() } }>Cerrar</Button>
                    { editing ?
                        <LoadingButton
                            disabled={ canSave === false }
                            loading={ saving }
                            variant="contained"
                            color={ 'primary' }
                            size='small'
                            endIcon={ <SaveOutlinedIcon/> }
                            loadingPosition="end"
                            onClick={() => confirmAndSave()}>
                                { saving ? 'Guardando' : 'Guardar' }
                        </LoadingButton>
                    : null }
                </DialogActions>
            </Dialog>
            <ImageViewer
                isOpen={openImageViewer}
                onClose={() => setOpenImageViewer(false)}
                images={currentDetail?.images} />
            <Dialog open={confirmFinishDialogOpen} onClose={() => setConfirmFinishDialogOpen(false)}>
                <DialogTitle>Finalizar entrega</DialogTitle>
                <DialogContent>
                    <Typography>
                        Esta acción marcará la entrega como <strong>Finalizada</strong>. ¿Deseas continuar?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmFinishDialogOpen(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setConfirmFinishDialogOpen(false);
                            saveDeliveryDetails(true); // guardamos y cambiamos a "finished"
                        }}>
                        Finalizar
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}

export default DeliveryDetails;
